import { createContext } from 'react';

import { EPaymentItemType, ETelegramChannelType } from 'types';

import { ESettingsAliases, ISettingsContext } from './interfaces';

export const CUSTOMIZATION_DEFAULT_DATA: Pick<
  ISettingsContext['state']['settings'],
  | 'shellSkinAccentColor'
  | 'shellSkinAdditionalColor'
  | 'shellSkinBackgroundColor'
  | 'shellSkinLogo'
  | 'shellBgSkin'
> = {
  shellSkinAccentColor: '#7C62FF',
  shellSkinAdditionalColor: '#000000',
  shellSkinBackgroundColor: '#232323',
  shellSkinLogo: '',
  shellBgSkin: '',
};

export const INITIAL_CONTEXT: ISettingsContext = {
  state: {
    currencies: [],
    settings: {
      autorun: [],
      lastUploadedFileId: 0,
      clubName: '',
      isWhiteListEnabled: false,
      whiteList: [],
      action: 3,
      timeout: 3,
      visibleDiscs: '0x0',
      shouldUseClientDiscount: true,
      selfHostChange: false,
      hostGroupSwitch: false,
      hostGroupSwitchPausable: false,
      shellGoodsDisplayEnabled: false,
      shouldPrintReport: false,
      printReportAllGoods: false,
      runPerMinuteOnLogin: false,
      runPerMinuteOnSessionEnd: false,
      isChromeDownloadRestrict: false,
      isRefundEnabled: false,
      refundHeadroom: 5,
      isCashboxEnabled: false,
      selectedCashboxINN: null,
      taxes: [],
      isDepositRefillEnabled: false,
      blacklist: [],
      telegramChannelId: {
        id: '',
        title: '',
        type: ETelegramChannelType.PRIVATE,
      },
      telegramToken: '',
      telegramNotificationTypes: {
        workshifts: '0',
        user_deposit: '0',
        user_discount: '0',
        refund: '0',
        bonus: '0',
        shell_status: '0',
        cash_order: '0',
        review: '1',
        warehouse: '0',
        security: '0',
        worker_auth: '0',
        low_stock: '1',
        loyalty_program: '0',
        advertisement: '0',
      },
      isClubRulesEnabled: false,
      clubRules: '',
      externalStorages: false,
      currency: {
        value: 'RUB',
        sign: '',
      },
      screenSaver: null,
      screenSaverTimeout: 5,
      feedbackEnabled: false,
      callAdminTimeout: 15,
      callAdmin: false,
      autoTurnoffEnabled: false,
      autoTurnoffTimeout: 10,
      bookingStrictMode: false,
      bookingStrictModeHeadroom: 5,
      bookingExpirationThreshold: 15,
      remoteControlCCBoot: false,
      remoteControlEnabled: false,
      remoteControlPassword: '',
      removeControlPasswordUpdated: null,
      restrictVirtualHostDepositPayment: false,
      shellLanguage: 'EN',
      mouseUtil: false,
      soundUtil: false,
      videoUtil: false,
      onlineBooking: false,
      onlineBookingDurationMin: 1,
      onlineBookingDurationMax: 24,
      onlineBookingCancel: false,
      onlineBookingCancelUntil: 0,
      onlineBookingPenalty: 0,
      onlineBookingBusy: false,
      onlineBookingMultipleHost: false,
      cloudPayments: false,
      publicId: null,
      timezone: 0,
      holidayTariffsEnabled: false,
      holidayTariffsDates: [],
      tinkoffSbpEnabled: false,
      showSeans: false,
      postPaymentEnabled: false,
      postPaymentMaxDuration: 120,
      onlineBookingPerMinuteEndingEnabled: false,
      clientRatingEnabled: false,
      clientRatingShellEnabled: false,
      isBonusPaymentTariffsEnabled: false,
      isUseDepositFirstEnabled: false,
      isUsagePercent: 0,
      countryCode: 'RU',
      onlineBookingDiscount: 5,
      bookingBlockDuration: 0,
      onlinePaymentsMinLimit: 10,
      lowStockThreshold: 5,
      bonusGoodsEnabled: false,
      bonusServicesEnabled: false,
      cloudPaymentsVat: null,
      cashierName: '',
      depositRefillTitle: null,
      lowStockEnabled: false,
      isBonusSystemEnabled: false,
      clientRegister: false,
      registerWithoutVerification: false,
      kaspiOnlineEnabled: false,
      kaspiOnlineBin: null,
      stripeEnabled: false,
      stripeSecretKey: '',
      stripePublishableKey: '',
      screenSmartKiosk: null,
      screenSmartTV: null,
      ...CUSTOMIZATION_DEFAULT_DATA,
      tipsValues: [],
      tipsEnabled: false,
      fastLoginAutostartSessionEnabled: false,
      fastLoginAutostartSessionTariffId: null,
      hostMap: '',
      hardwareChanges: false,
      lightShell: false,
      bePaidEnabled: false,
      bePaidPublishableKey: '',
      bePaidSecretKey: '',
      bePaidShopId: '',
    },
    isInitialized: false,
    isLoading: false,
  },
  disable: new Set<ESettingsAliases>(),
  actions: {
    loadSettings: () => Promise.reject(),
    setSetting: () => Promise.reject(),
    setSettings: () => Promise.reject(),
  },
};

export const SettingsContext = createContext<ISettingsContext>(INITIAL_CONTEXT);

export const ENTITIES_CONFIG: Array<{
  entity: EPaymentItemType;
  ndsAlias: ESettingsAliases;
  variantAlias: ESettingsAliases;
}> = [
  {
    entity: EPaymentItemType.GOOD,
    ndsAlias: ESettingsAliases.GOODS_NDS,
    variantAlias: ESettingsAliases.GOODS_TAX_SYSTEM,
  },
  {
    entity: EPaymentItemType.SERVICE,
    ndsAlias: ESettingsAliases.SERVICES_NDS,
    variantAlias: ESettingsAliases.SERVICES_TAX_SYSTEM,
  },
  {
    entity: EPaymentItemType.TARIFF,
    ndsAlias: ESettingsAliases.TARIFFS_NDS,
    variantAlias: ESettingsAliases.TARIFFS_TAX_SYSTEM,
  },
  {
    entity: EPaymentItemType.DEPOSIT,
    ndsAlias: ESettingsAliases.DEPOSIT_NDS,
    variantAlias: ESettingsAliases.DEPOSIT_TAX_SYSTEM,
  },
];
